/**
 * Engine events → choreography.
 *
 * The queue does not carry raw `GameEvent`s, and that is the whole point of this
 * file. A deal is sixteen cards in one motion; pushing four `CARDS_DEALT` events
 * would serialize into four sequential deals, each waiting for the last, because
 * the director runs one entry at a time by design. So the batch is folded into
 * *choreography* — one `deal`, one `discard`, one `draw` — before it is queued.
 *
 * It also captures, synchronously, the one thing that cannot be recovered later:
 * where the cards that are about to leave a hand currently are. By the time the
 * director dequeues the discard, React has removed them.
 *
 * Card events arrive twice for their owner — a public twin carrying only a count
 * and a private one carrying the cards (`emitCardEvent`, backend.md §1.10).
 * Folding by seat collapses the pair without the caller having to know it exists.
 */

import type { CardId, GameEvent, Seat } from '@ordago/rules';

import { useStore } from '../../store';

import { director } from './director';
import { handCards } from './dom';
import type { Origin, SeatCount } from './timelines/cards';
import { toRect } from './util';

type GroupKind = 'deal' | 'discard' | 'draw';

const GROUPED: Partial<Record<GameEvent['type'], GroupKind>> = {
  CARDS_DEALT: 'deal',
  DISCARDED: 'discard',
  DREW: 'draw',
};

/**
 * The cards about to leave a seat, with the rects they occupy right now.
 *
 * For my own seat the private twin names them, so the exact nodes are found by
 * identity. For anyone else only the count is public — by rule — and the last
 * `count` slots stand in. Visually identical, and it stays true to the rule that
 * this client knows nothing it was not sent.
 */
function captureOrigins(
  seat: Seat,
  mySeat: Seat | null,
  count: number,
  cards: CardId[] | undefined,
): Origin[] {
  const els = handCards(seat, mySeat);
  const chosen =
    seat === mySeat && cards !== undefined && cards.length > 0
      ? cards
          .map((id) => els.find((el) => el.dataset['cardId'] === id))
          .filter((el): el is HTMLElement => el !== undefined)
      : els.slice(Math.max(0, els.length - count));

  return chosen.map((el) => {
    const id = el.dataset['cardId'];
    const known = id !== undefined && !id.startsWith('back:');
    return {
      rect: toRect(el.getBoundingClientRect()),
      ...(known ? { id: id as CardId } : {}),
      faceDown: el.dataset['faceDown'] === 'true',
      size: el.dataset['size'] === 'opp' ? ('opp' as const) : ('own' as const),
    };
  });
}

export function queueAnimations(events: GameEvent[], mySeat: Seat | null): void {
  const mano = useStore.getState().room.mano ?? 0;

  let kind: GroupKind | null = null;
  let by: SeatCount[] = [];
  let origins: Origin[] = [];

  const flush = (): void => {
    if (kind === null) return;
    if (kind === 'discard') {
      if (origins.length > 0) director.push({ t: 'discard', origins });
    } else if (by.length > 0) {
      director.push({ t: kind, by, mano });
    }
    kind = null;
    by = [];
    origins = [];
  };

  for (const event of events) {
    const group = GROUPED[event.type];

    if (group !== undefined) {
      // A different kind of card movement ends the previous group, which is
      // what preserves "all four discarded, then all four drew" as two beats.
      if (kind !== null && kind !== group) flush();
      kind = group;

      if (event.type === 'CARDS_DEALT' || event.type === 'DREW' || event.type === 'DISCARDED') {
        const existing = by.find((b) => b.seat === event.seat);
        if (existing === undefined) {
          by.push({
            seat: event.seat,
            count: event.count,
            ...(event.cards.length > 0 ? { cards: event.cards } : {}),
          });
          // Only on the first sighting of a seat: the twin would measure the
          // same nodes again, and for a discard the rects must be the ones from
          // before anything moved.
          if (group === 'discard') {
            origins.push(...captureOrigins(event.seat, mySeat, event.count, undefined));
          }
        } else if (event.cards.length > 0) {
          // The private twin arrived second and it is the one that names cards.
          existing.cards = event.cards;
          if (group === 'discard' && event.seat === mySeat) {
            // Re-measure by identity now that the cards are known, replacing
            // the count-based guess made a moment ago for this seat.
            const mine = captureOrigins(event.seat, mySeat, event.count, event.cards);
            if (mine.length > 0) {
              origins = origins.filter((o) => o.size !== 'own');
              origins.push(...mine);
            }
          }
        }
      }
      continue;
    }

    flush();

    if (event.type === 'HAND_STARTED') {
      director.push({ t: 'shuffle' });
    } else if (event.type === 'BET') {
      if (event.kind === 'ORDAGO') {
        director.push({ t: 'ordago', seat: event.seat });
      } else if (event.kind === 'ENVIDO' && event.amount !== undefined) {
        director.push({ t: 'chips', seat: event.seat, amount: event.amount });
      } else if (event.kind === 'QUIERO') {
        const staked = useStore.getState().room.betting?.currentAmount ?? 0;
        if (staked > 0) director.push({ t: 'chips', seat: event.seat, amount: staked });
      }
    } else if (event.type === 'SCORED') {
      director.push({ t: 'scored', team: event.team, points: event.points });
    } else if (event.type === 'GAME_ENDED') {
      director.push({ t: 'celebrate', winner: event.winner, byOrdago: event.byOrdago });
    }
  }

  flush();
}
